import { FC, useState } from 'react'
import { MeetingRequestDto } from '../../../../types'
import { useTranslation } from 'react-i18next'
import { SmartAvatar } from '../../../avatar/SmartAvatar'
import { MeetingTag } from '../../../../components/custom_components/MeetingTag'
import { RequestMeetingDeclineModal } from '../../../meeting/cancel/RequestMeetingDeclineModal'
import { MeetingInviteModal } from '../../../meeting/invite/MeetingInviteModal'
import useWithdrawMeetingRequest from '../../../../graphql/meeting/hooks/useWithdrawMeetingRequest'
import PerFlexRow from '../../../../components/custom_components/FlexRow'
import PerSpacer from '../../../../components/custom_components/Spacer'
import PerText from '../../../../components/custom_components/Text'
import PerButton from '../../../../components/custom_components/Button'
import PerIcon from '../../../../components/custom_components/Icon'
import useDeclineMeetingRequest from '../../../../graphql/meeting/hooks/useDeclineMeetingRequest'
import { showNotification } from '../../../../components/custom_components/Notification/notification'
import { useMeetingRequestOptionsStore } from '../../../../graphql/requestMeetingOptions/MeetingRequestOptionsStore'

type RequestedMeetingTileProps = {
  request: MeetingRequestDto
  isManager: boolean | undefined
  isReviewer: boolean | undefined
}

export const RequestedMeetingTile: FC<RequestedMeetingTileProps> = ({ request, isManager, isReviewer }) => {
  const [t] = useTranslation()
  const { withdrawMeeting } = useWithdrawMeetingRequest()
  const [acceptModalVisible, setAcceptModalVisible] = useState<boolean>(false)
  const [declineModalVisible, setDeclineModalVisible] = useState<boolean>(false)
  const { declineMeetingRequest } = useDeclineMeetingRequest()

  const onDeclineRequest = async (requestMeetingId: number, reasonOfRejection: string): Promise<string | undefined> => {
    setDeclineModalVisible(false)
    const { errors } = await declineMeetingRequest(requestMeetingId, reasonOfRejection, undefined, request.requester.id)
    if (!!errors && !!errors.length) {
      return t('global.notifications.error.description')
    } else {
      showNotification({
        type: 'success',
        message: t(`meetingView.requestedMeetings.modal.notifications.success.title`),
        description: t(`meetingView.requestedMeetings.modal.notifications.success.description`, {
          requesterFirstName: request.requester.firstName,
        }),
      })
      return undefined
    }
  }

  const [isProcessing, toggleProcessing] = useState(false)
  const meetingOptionsLoading = useMeetingRequestOptionsStore((state) => state.loading)

  return (
    <>
      {acceptModalVisible && (
        <MeetingInviteModal
          visible={acceptModalVisible}
          onCancel={() => setAcceptModalVisible(false)}
          employeeList={[request.requester]}
          requestedMeeting={request}
        />
      )}
      {declineModalVisible && (
        <RequestMeetingDeclineModal
          isOpen={declineModalVisible}
          onClose={() => setDeclineModalVisible(false)}
          onDeclineRequest={async (requestMeetingId, reasonOfRejection) => await onDeclineRequest(requestMeetingId, reasonOfRejection)}
          requestedMeeting={request}
        />
      )}
      <PerFlexRow align="middle" className="meetingTile" mb="xxs" noWrap>
        <SmartAvatar employeeId={request.requester.id} firstName={request.requester.firstName} lastName={request.requester.lastName} />
        <PerSpacer ml="xxs">
          <MeetingTag type={request.type} />
          <PerText mt="xxxs" fontWeight="bold">
            <PerText display="inline" color="secondary">
              {t('dashboard.employee.meetings.requestTileHeadline')}
            </PerText>
          </PerText>
          {isReviewer ? (
            <PerText color="secondary" fontSize="s">
              {t('global.you')}
            </PerText>
          ) : isManager ? (
            <PerText color="secondary" fontSize="s">
              {request.requester.firstName} {request.requester.lastName}
            </PerText>
          ) : (
            <PerText color="secondary" fontSize="s">
              {request.reviewer.firstName} {request.reviewer.lastName}
            </PerText>
          )}
        </PerSpacer>
      </PerFlexRow>
      {isReviewer ? (
        <PerFlexRow direction="row" mt="xxs" align="middle" noWrap>
          <PerButton
            text="accept"
            before="check_stroke"
            color="success"
            variant="pastel"
            size="small"
            width="fullWidth"
            onClick={() => setAcceptModalVisible(true)}
          />
          <PerSpacer mr="xxs" />
          <PerButton
            text="decline"
            before="close_stroke"
            color="error"
            variant="pastel"
            size="small"
            width="fullWidth"
            onClick={() => setDeclineModalVisible(true)}
          />
        </PerFlexRow>
      ) : isManager ? (
        <PerFlexRow direction="row" mt="xxs" align="middle" noWrap>
          <PerIcon name="waiting_stroke" color="info" size={16} variant="decoration" className="per-flexRow--fixed" />
          <PerText color="info" fontSize="s" ml="xxs" className="per-flexRow--shrink">
            {t('dashboard.employee.meetings.requestTileInfo', { managerName: request.reviewer.firstName })}
          </PerText>
        </PerFlexRow>
      ) : (
        <PerFlexRow direction="row" mt="xxs" align="middle" noWrap>
          <PerButton
            text={t('dashboard.employee.meetings.withdrawMeetingRequest')}
            before="close_stroke"
            color="error"
            size="small"
            width="fullWidth"
            variant="pastel"
            disabled={isProcessing || meetingOptionsLoading}
            isProcessing={{ active: isProcessing }}
            onClick={async () => {
              toggleProcessing(true)
              try {
                const res = await withdrawMeeting(request.requester.id, request.id)
                if (!res.errors) {
                  const refetchPromise = useMeetingRequestOptionsStore.getState().refetch(request.requester.id)
                  await refetchPromise
                }
              } finally {
                toggleProcessing(false)
              }
            }}
          />
        </PerFlexRow>
      )}
    </>
  )
}
